// A small markdown renderer, written rather than installed.
//
// It covers what people actually type into a post box: headings, bold, italic,
// inline code, links, bullet and numbered lists, block quotes, fenced code and a
// rule. Everything is built as React elements, so no string of HTML is ever
// handed to the DOM and a post cannot carry markup of its own.
//
// A link is rendered only when it points at http or https, and it opens in a new
// tab with the referrer withheld.

import type { ReactNode } from 'react';
import { Fragment } from 'react';

const INLINE =
  /(`[^`]+`)|(\*\*[^*]+\*\*)|(__[^_]+__)|(\*[^*\n]+\*)|(_[^_\n]+_)|(~~[^~]+~~)|(\[[^\]]+\]\([^)\s]+\))|(https?:\/\/[^\s<>]+)/;

function safeHref(url: string): string | null {
  return /^https?:\/\/\S+$/i.test(url) ? url : null;
}

function inline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let n = 0;

  while (rest.length > 0) {
    const match = rest.match(INLINE);
    if (!match || match.index === undefined) {
      out.push(rest);
      break;
    }
    if (match.index > 0) out.push(rest.slice(0, match.index));
    const token = match[0];
    const key = `${keyBase}-${n}`;
    n += 1;

    if (token.startsWith('`')) {
      out.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith('**') || token.startsWith('__')) {
      out.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('~~')) {
      out.push(<del key={key}>{token.slice(2, -2)}</del>);
    } else if (token.startsWith('[')) {
      const close = token.indexOf('](');
      const label = token.slice(1, close);
      const href = safeHref(token.slice(close + 2, -1));
      out.push(
        href ? (
          <a key={key} href={href} target="_blank" rel="noreferrer noopener">
            {label}
          </a>
        ) : (
          <Fragment key={key}>{label}</Fragment>
        ),
      );
    } else if (token.startsWith('http')) {
      out.push(
        <a key={key} href={token} target="_blank" rel="noreferrer noopener">
          {token}
        </a>,
      );
    } else {
      out.push(<em key={key}>{token.slice(1, -1)}</em>);
    }

    rest = rest.slice(match.index + token.length);
  }

  return out;
}

/** Turns one markdown string into elements. */
export function renderMarkdown(source: string): ReactNode {
  const lines = String(source ?? '').replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];

  let bullets: string[] | null = null;
  let numbers: string[] | null = null;
  let quote: string[] | null = null;
  let fence: string[] | null = null;

  const flush = () => {
    const at = blocks.length;
    if (bullets) {
      blocks.push(
        <ul key={`ul-${at}`}>
          {bullets.map((item, i) => (
            <li key={i}>{inline(item, `ul-${at}-${i}`)}</li>
          ))}
        </ul>,
      );
      bullets = null;
    }
    if (numbers) {
      blocks.push(
        <ol key={`ol-${at}`}>
          {numbers.map((item, i) => (
            <li key={i}>{inline(item, `ol-${at}-${i}`)}</li>
          ))}
        </ol>,
      );
      numbers = null;
    }
    if (quote) {
      blocks.push(<blockquote key={`bq-${at}`}>{inline(quote.join(' '), `bq-${at}`)}</blockquote>);
      quote = null;
    }
  };

  for (const [index, raw] of lines.entries()) {
    const line = raw.trimEnd();

    if (line.trim().startsWith('```')) {
      if (fence) {
        blocks.push(
          <pre key={`pre-${index}`}>
            <code>{fence.join('\n')}</code>
          </pre>,
        );
        fence = null;
      } else {
        flush();
        fence = [];
      }
      continue;
    }
    if (fence) {
      fence.push(raw);
      continue;
    }

    if (line.trim() === '') {
      flush();
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      flush();
      const level = heading[1].length;
      const text = inline(heading[2], `h-${index}`);
      if (level === 1) blocks.push(<h3 key={`h-${index}`}>{text}</h3>);
      else if (level === 2) blocks.push(<h4 key={`h-${index}`}>{text}</h4>);
      else blocks.push(<h5 key={`h-${index}`}>{text}</h5>);
      continue;
    }

    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      flush();
      blocks.push(<hr key={`hr-${index}`} />);
      continue;
    }

    const bullet = line.match(/^\s*[-*+]\s+(.*)$/);
    if (bullet) {
      if (numbers || quote) flush();
      bullets = bullets ?? [];
      bullets.push(bullet[1]);
      continue;
    }

    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (numbered) {
      if (bullets || quote) flush();
      numbers = numbers ?? [];
      numbers.push(numbered[1]);
      continue;
    }

    const quoted = line.match(/^\s*>\s?(.*)$/);
    if (quoted) {
      if (bullets || numbers) flush();
      quote = quote ?? [];
      quote.push(quoted[1]);
      continue;
    }

    flush();
    blocks.push(<p key={`p-${index}`}>{inline(line, `p-${index}`)}</p>);
  }

  if (fence) {
    blocks.push(
      <pre key="pre-end">
        <code>{fence.join('\n')}</code>
      </pre>,
    );
  }
  flush();

  return <div className="md">{blocks}</div>;
}

/** The plain text of a markdown string, for a listing that shows an excerpt. */
export function markdownExcerpt(source: string, limit = 220): string {
  const flat = String(source ?? '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[#>*_`~]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return flat.length > limit ? `${flat.slice(0, limit)}...` : flat;
}
